import { useId, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useApi } from '@/api/apiContext';
import { collectionEventsQueryOptions } from '@/api/endpoints/collections';
import { messages } from '@/i18n';
import { formatDateTime } from './driverCollectionsData';

/**
 * Renders spec 4.5's event chain inside a collected card (UI plan §3.4).
 * Fetched on expand, not with the list — a per-row call for every card in a
 * paged list would multiply requests. `enabled: expanded` plus react-query's
 * own cache means re-expanding is free. Read-only: no controls of any kind.
 */
export function CollectionHistory({
  collectionId,
  unit,
  eventCount,
}: {
  collectionId: string;
  unit: string;
  eventCount: number;
}) {
  const t = messages.collectionHistory;
  const api = useApi();
  const [expanded, setExpanded] = useState(false);
  const regionId = useId();

  const eventsQuery = useQuery({
    ...collectionEventsQueryOptions(api, collectionId),
    enabled: expanded,
  });
  const events = eventsQuery.data ?? [];

  return (
    <div className="mt-3">
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-expanded={expanded}
        aria-controls={regionId}
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />}
        {t.toggle} ({eventCount})
      </Button>

      {expanded ? (
        <div id={regionId} className="border-border/60 bg-secondary/20 mt-3 rounded-md border p-3">
          {eventsQuery.isLoading ? (
            <p className="text-muted-foreground text-sm">{t.loading}</p>
          ) : eventsQuery.isError ? (
            <div className="flex items-center justify-between gap-3">
              <p className="text-muted-foreground text-sm">{t.loadError}</p>
              <Button variant="outline" size="sm" onClick={() => void eventsQuery.refetch()}>
                {t.retry}
              </Button>
            </div>
          ) : events.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t.empty}</p>
          ) : (
            <ol className="space-y-3">
              {events.map((event) => (
                <li key={event.id} className="border-border/40 border-l-2 pl-3 text-sm">
                  <p className="flex flex-wrap items-center gap-2 font-medium">
                    {event.type === 'Correction' ? t.eventCorrected : t.eventCollected}
                    <span className="text-muted-foreground font-normal">
                      {event.collectedUnits} {unit}
                    </span>
                    {event.hasDeviation ? (
                      <Badge variant="warning" className="text-[10px]">
                        {t.deviationTag}
                      </Badge>
                    ) : null}
                  </p>
                  <p className="text-muted-foreground mt-0.5">
                    {event.collectedBy} · {event.organisationName} ·{' '}
                    {formatDateTime(event.collectedAt)}
                  </p>
                  {event.reason ? (
                    <p className="mt-1 italic">
                      {t.reasonLabel}: &ldquo;{event.reason}&rdquo;
                    </p>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </div>
      ) : null}
    </div>
  );
}
